"use client";

import { useCallback, useEffect, useState } from "react";
import { X } from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogTitle,
} from "@/shadcn-components/ui/dialog";
import * as VisuallyHidden from "@radix-ui/react-visually-hidden";
import { Button } from "@/shadcn-components/ui/button";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/shadcn-components/ui/alert-dialog";
import {
  deleteProject,
  listBuilds,
  listProjects,
} from "@/lib/custom-indicators/db";
import { projectToRecord } from "@/lib/custom-indicators/store";
import type { CreateProjectDraft } from "@/lib/custom-indicators/create-draft";
import type {
  CustomIndicatorBuild,
  CustomIndicatorProject,
  CustomIndicatorRecord,
} from "@/lib/custom-indicators/types";
import {
  downloadBundle,
  exportProjectBinaryBundle,
  exportProjectBundle,
  findImportNameConflict,
  importProjectBundle,
  parseBundleJson,
} from "@/lib/custom-indicators/bundle";
import { installMarketplaceTemplate } from "@/lib/custom-indicators/marketplace-install";
import {
  canExportSourceBundle,
  isProjectEditable,
} from "@/lib/custom-indicators/project-ownership";
import { getTemplateById } from "@/lib/custom-indicators/templates";
import {
  ensureCompiledFromBuild,
  warmProjectFromPersistedBuild,
} from "@/lib/custom-indicators/hydrate-build";
import {
  ensureIndicatorRuntime,
  preloadIndicatorRuntime,
} from "@/lib/custom-indicators/runtime/IndicatorRuntimeManager";
import CustomIndicatorEditorDialog from "./CustomIndicatorEditorDialog";
import CustomIndicatorConfig from "./CustomIndicatorConfig";
import StudioCreateWizard from "./StudioCreateWizard";
import StudioIndicatorBrowser from "./StudioIndicatorBrowser";
import type { ChartPanelId } from "@/vybe-trader-client/chart/stores/chartPanelTypes";
import { useChartIndicatorsStore } from "@/lib/chart-indicators";
import { useMainChartStore } from "@/lib/main-chart";
import type { IndicatorInstance } from "@/lib/chart-indicators/registry";
import { cn } from "@/lib/utils";

interface VybeStudioDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  isDesktop?: boolean;
  chartPanelId?: ChartPanelId;
  /** When omitted, add-to-chart uses main chart / pane stores directly. */
  onAddToChart?: (record: CustomIndicatorRecord) => void;
}

type StudioView = "gallery" | "create" | "chart-config";

export default function VybeStudioDialog({
  open,
  onOpenChange,
  isDesktop = false,
  chartPanelId,
  onAddToChart,
}: VybeStudioDialogProps) {
  const [view, setView] = useState<StudioView>("gallery");
  const [projects, setProjects] = useState<CustomIndicatorProject[]>([]);
  const [buildsByProject, setBuildsByProject] = useState<
    Record<string, CustomIndicatorBuild>
  >({});
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const [editorOpen, setEditorOpen] = useState(false);
  const [editingRecord, setEditingRecord] =
    useState<CustomIndicatorRecord | null>(null);
  const [createDraft, setCreateDraft] = useState<CreateProjectDraft | null>(
    null
  );

  const [deleteTarget, setDeleteTarget] = useState<CustomIndicatorProject | null>(
    null
  );
  const [importConflict, setImportConflict] = useState<{
    name: string;
    existingProjectId: string;
    bundleText: string;
  } | null>(null);

  const [chartConfigRecord, setChartConfigRecord] =
    useState<CustomIndicatorRecord | null>(null);

  const addWindow = useChartIndicatorsStore((s) => s.addWindow);
  const addMainIndicator = useMainChartStore((s) => s.addIndicator);

  const reload = useCallback(async () => {
    setIsLoading(true);
    setError(null);
    try {
      const [projList, buildList] = await Promise.all([
        listProjects(),
        listBuilds(),
      ]);
      setProjects(projList);
      const byProject: Record<string, CustomIndicatorBuild> = {};
      for (const b of buildList) {
        byProject[b.projectId] = b;
      }
      setBuildsByProject(byProject);
    } catch (e) {
      setError(e instanceof Error ? e.message : "Failed to load projects");
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    if (!open) return;
    setView("gallery");
    void reload();
    preloadIndicatorRuntime();
    void ensureIndicatorRuntime();
  }, [open, reload]);

  const openEditorForProject = (project: CustomIndicatorProject) => {
    if (!isProjectEditable(project)) {
      setError("This indicator is read-only and cannot be opened in the editor.");
      return;
    }
    setCreateDraft(null);
    const entry = project.entryContent ?? "";
    setEditingRecord(projectToRecord(project, entry));
    setEditorOpen(true);
  };

  const handleWizardComplete = (draft: CreateProjectDraft) => {
    setEditingRecord(null);
    setCreateDraft(draft);
    setEditorOpen(true);
    setView("gallery");
  };

  const handleCreateFromTemplate = (draft: CreateProjectDraft) => {
    setEditingRecord(null);
    setCreateDraft(draft);
    setEditorOpen(true);
  };

  const handleAddToChartClick = async (record: CustomIndicatorRecord) => {
    try {
      const loaded = await ensureCompiledFromBuild(record.id);
      if (!loaded?.success) {
        setError(
          "This indicator has no loaded build yet. Wait a moment after import, or re-import the .vybeind file."
        );
        return;
      }
    } catch (e) {
      setError(e instanceof Error ? e.message : "Failed to load indicator build");
      return;
    }
    if (onAddToChart) {
      onAddToChart(record);
      onOpenChange(false);
      return;
    }
    setChartConfigRecord(record);
    setView("chart-config");
  };

  const handleApplyChartConfig = (instance: IndicatorInstance) => {
    if (instance.createsNewChart) {
      addWindow(instance as Parameters<typeof addWindow>[0]);
    } else {
      addMainIndicator(instance);
    }
    setChartConfigRecord(null);
    setView("gallery");
    onOpenChange(false);
  };

  const handleDelete = async (project: CustomIndicatorProject) => {
    await deleteProject(project.id);
    await reload();
    setDeleteTarget(null);
  };

  const handleExportSource = async (projectId: string) => {
    try {
      const { blob, filename } = await exportProjectBundle(projectId);
      downloadBundle(blob, filename);
    } catch (e) {
      setError(e instanceof Error ? e.message : "Export failed");
    }
  };

  const handleExportBinary = async (projectId: string) => {
    try {
      const { blob, filename } = await exportProjectBinaryBundle(projectId);
      downloadBundle(blob, filename);
    } catch (e) {
      setError(e instanceof Error ? e.message : "Export failed");
    }
  };

  const handleInstallTemplate = async (templateId: string) => {
    const template = getTemplateById(templateId);
    if (!template) {
      setError("Template not found");
      return;
    }
    setError(null);
    try {
      const result = await installMarketplaceTemplate(template);
      if (!result.ok) {
        setError(result.error);
        return;
      }
      // Install compiles in-process; only hydrate from IDB when re-opening an existing install.
      const loaded = result.alreadyInstalled
        ? await warmProjectFromPersistedBuild(result.projectId)
        : await ensureCompiledFromBuild(result.projectId);
      if (!loaded?.success) {
        setError(
          "Installed but the compiled assembly could not be loaded. Try Add to chart again."
        );
      }
      await reload();
    } catch (e) {
      setError(e instanceof Error ? e.message : "Install failed");
    }
  };

  const handleImportFile = async (file: File) => {
    setError(null);
    try {
      const text = await file.text();
      const bundle = parseBundleJson(text);
      const conflict = await findImportNameConflict(bundle.project.name.trim());
      if (conflict) {
        setImportConflict({
          name: conflict.existingName,
          existingProjectId: conflict.existingProjectId,
          bundleText: text,
        });
        return;
      }
      const result = await importProjectBundle(bundle);
      if (!result.ok) {
        setError("error" in result ? result.error : "Import failed");
        return;
      }
      const warmed = await warmProjectFromPersistedBuild(result.projectId);
      if (!warmed?.success) {
        setError(
          "Imported but the compiled assembly could not be loaded into the runtime. Try importing again."
        );
      }
      await reload();
    } catch (e) {
      setError(e instanceof Error ? e.message : "Import failed");
    }
  };

  const confirmImportOverwrite = async () => {
    if (!importConflict) return;
    try {
      const bundle = parseBundleJson(importConflict.bundleText);
      const result = await importProjectBundle(bundle, {
        overwriteProjectId: importConflict.existingProjectId,
      });
      if (!result.ok) {
        setError("error" in result ? result.error : "Import failed");
        return;
      }
      const warmed = await warmProjectFromPersistedBuild(result.projectId);
      if (!warmed?.success) {
        setError(
          "Imported but the compiled assembly could not be loaded into the runtime. Try importing again."
        );
      }
      await reload();
    } catch (e) {
      setError(e instanceof Error ? e.message : "Import failed");
    } finally {
      setImportConflict(null);
    }
  };

  const dialogSizeClass = isDesktop
    ? "left-[3vw] top-[3vh] h-[94vh] max-h-[94vh] w-[94vw] max-w-[94vw] translate-x-0 translate-y-0 sm:max-w-[94vw]"
    : "left-0 top-0 h-[100dvh] max-h-[100dvh] w-full max-w-full translate-x-0 translate-y-0 sm:max-w-full rounded-none";

  if (view === "chart-config" && chartConfigRecord && open && !editorOpen) {
    return (
      <Dialog open onOpenChange={onOpenChange}>
        <DialogContent
          className={cn(
            "flex flex-col gap-0 overflow-hidden p-0",
            dialogSizeClass
          )}
        >
          <CustomIndicatorConfig
            record={chartConfigRecord}
            onBack={() => {
              setChartConfigRecord(null);
              setView("gallery");
            }}
            onApply={handleApplyChartConfig}
          />
        </DialogContent>
      </Dialog>
    );
  }

  return (
    <>
      <Dialog open={open && !editorOpen} onOpenChange={onOpenChange}>
        <DialogContent
          className={cn(
            "flex flex-col gap-0 overflow-hidden p-0",
            dialogSizeClass
          )}
          onPointerDownOutside={(e) => e.preventDefault()}
        >
          <VisuallyHidden.Root asChild>
            <DialogTitle>Vybe Studio</DialogTitle>
          </VisuallyHidden.Root>
          <VisuallyHidden.Root asChild>
            <DialogDescription>
              Manage custom trading indicator projects.
            </DialogDescription>
          </VisuallyHidden.Root>

          <header className="flex h-12 shrink-0 items-center gap-2 border-b border-border px-4">
            <span className="text-sm font-semibold tracking-tight">
              Vybe Studio
            </span>
            <span className="text-xs text-muted-foreground">
              Custom indicators
            </span>
            <div className="flex-1" />
            <Button
              variant="ghost"
              size="icon"
              className="h-8 w-8"
              onClick={() => onOpenChange(false)}
              aria-label="Close"
            >
              <X className="h-4 w-4" />
            </Button>
          </header>

          {view === "gallery" ? (
            <StudioIndicatorBrowser
              projects={projects}
              buildsByProject={buildsByProject}
              isLoading={isLoading}
              error={error}
              onNew={() => setView("create")}
              onImportFile={(file) => void handleImportFile(file)}
              onAddToChart={handleAddToChartClick}
              onOpenEditor={openEditorForProject}
              onExportSource={(projectId) => void handleExportSource(projectId)}
              onExportBinary={(projectId) => void handleExportBinary(projectId)}
              onDelete={(project) => setDeleteTarget(project)}
              onCreateFromTemplate={handleCreateFromTemplate}
              onInstallTemplate={(templateId) =>
                void handleInstallTemplate(templateId)
              }
              canExportSource={canExportSourceBundle}
            />
          ) : (
            <StudioCreateWizard
              onCancel={() => setView("gallery")}
              onComplete={handleWizardComplete}
            />
          )}
        </DialogContent>
      </Dialog>

      <CustomIndicatorEditorDialog
        open={editorOpen}
        onOpenChange={(next) => {
          setEditorOpen(next);
          if (!next) {
            setEditingRecord(null);
            setCreateDraft(null);
            void reload();
          }
        }}
        record={editingRecord}
        createDraft={createDraft}
        chartPanelId={chartPanelId}
      />

      <AlertDialog
        open={Boolean(deleteTarget)}
        onOpenChange={(o) => !o && setDeleteTarget(null)}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete project</AlertDialogTitle>
            <AlertDialogDescription>
              Permanently delete &quot;{deleteTarget?.name}&quot; and its saved
              build? This cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
              onClick={() =>
                deleteTarget && void handleDelete(deleteTarget)
              }
            >
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <AlertDialog
        open={Boolean(importConflict)}
        onOpenChange={(o) => !o && setImportConflict(null)}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Overwrite existing project?</AlertDialogTitle>
            <AlertDialogDescription>
              A project named &quot;{importConflict?.name}&quot; already exists.
              Importing will replace its source files and saved build.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={() => void confirmImportOverwrite()}>
              Overwrite
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
}
